import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import IssuerDashboardNotificationItem from '@/modules/issuer-dashboard/components/items/IssuerDashboardNotificationItem.tsx';
import { useIssuerNotificationsPageQuery } from '@/modules/issuer-dashboard/queries/event-logs.ts';
import { ChartEmptyState, InfoCard, Spinner } from 'ui';
import { ArrowLeftGayIcon, ArrowLeftIcon } from 'ui/icons';

const itemsPerPage = 5;

const Notifications = () => {
  const [params] = useSearchParams();
  const registerId = params.get(REGISTER_ID_SEARCH_PARAM_KEY) || '';
  const [page, setPage] = useState<number>(1);

  useEffect(() => {
    setPage(1);
  }, [registerId]);

  const { data, isFetching, isLoading } = useIssuerNotificationsPageQuery(
    { page, page_size: itemsPerPage },
    registerId,
  );

  const notifications = data?.data || [];
  const totalItems = data?.total || 0;

  const firstItemShownNumber = (page - 1) * 5 + 1;
  const lastItemShownNumber = Math.min((page - 1) * 5 + 5, totalItems);

  const isPossibleGoBack = !isFetching && page > 1;
  const isPossibleGoForward = !isFetching && lastItemShownNumber < totalItems;

  const goToPreviousPage = useCallback(() => {
    setPage(page => page - 1);
  }, []);

  const goToNextPage = useCallback(() => {
    setPage(page => page + 1);
  }, []);

  const bottomContent = !!notifications.length && (
    <div className="w-full flex flex-row justify-between">
      <div className="text-sm text-neutral-500">
        {firstItemShownNumber} - {lastItemShownNumber} of {totalItems}
      </div>

      <div className="flex flex-row gap-6">
        {isFetching && <Spinner size="sm" />}
        <button onClick={goToPreviousPage} disabled={!isPossibleGoBack}>
          <img alt="back" src={isPossibleGoBack ? ArrowLeftIcon : ArrowLeftGayIcon} />
        </button>
        <button onClick={goToNextPage} disabled={!isPossibleGoForward}>
          <img
            alt="forward"
            src={isPossibleGoForward ? ArrowLeftIcon : ArrowLeftGayIcon}
            className="rotate-180"
          />
        </button>
      </div>
    </div>
  );

  return (
    <InfoCard title="Event logs" bottomAction={bottomContent}>
      <div className="w-full h-[404px] min-h-[250px]">
        {isLoading && (
          <div className="flex justify-center items-center font-medium text-lg">
            <ChartEmptyState isLoading={isLoading} />
          </div>
        )}
        {!isLoading && !notifications.length && (
          <div className="w-full h-full flex items-center justify-center">
            <p className="text-lg font-semibold text-center">
              Notifications about actions happening in your registry will appear here
            </p>
          </div>
        )}
        {!isLoading && !!notifications.length && (
          <div className="flex flex-col gap-4">
            {notifications.map(notification => (
              <IssuerDashboardNotificationItem key={notification.id} notification={notification} />
            ))}
          </div>
        )}
      </div>
    </InfoCard>
  );
};

export default Notifications;
